<template>
  <div class="knowledge-page">
    <div class="ui container">
      <div class="page-header m-padded-tb-big">
        <div class="header-wrapper">
          <div class="header-icon">
            <i class="book icon"></i>
          </div>
          <div class="header-content">
            <h2 class="header-title">AI 知识库</h2>
            <p class="header-subtitle">基于知识库的智能问答，为你提供精准解答</p>
          </div>
        </div>
      </div>

      <div class="ui top attached tabular menu" style="border-radius: 8px 8px 0 0; overflow: hidden;">
        <a class="item" :class="{active: activeTab === 'chat'}" @click="switchTab('chat')" style="transition: all 0.3s ease;">
          <i class="comments icon"></i>AI 对话
        </a>
        <a class="item" :class="{active: activeTab === 'search'}" @click="switchTab('search')" style="transition: all 0.3s ease;">
          <i class="search icon"></i>知识搜索
        </a>
      </div>

      <div class="ui bottom attached segment" style="border-radius: 0 0 8px 8px; min-height: 500px;">
        <div v-if="activeTab === 'chat'" class="chat-panel">
          <div class="chat-container">
            <ChatMessages :messages="messages" :loading="chatLoading" />
            <ChatInput :disabled="chatLoading" @send="handleSend" placeholder="输入你的问题..." />
          </div>
          <div class="chat-actions">
            <button class="ui tiny basic button" @click="handleNewSession">
              <i class="plus icon"></i>新对话
            </button>
            <button class="ui tiny basic button" @click="handleClearMessages" :disabled="messages.length === 0">
              <i class="trash icon"></i>清空
            </button>
          </div>
        </div>

        <div v-if="activeTab === 'search'" class="search-panel">
          <div class="search-box m-padded-tb-small">
            <div class="ui large action input" style="width: 100%; box-shadow: 0 2px 8px rgba(0,0,0,0.06); border-radius: 8px; overflow: hidden;">
              <input
                type="text"
                v-model="searchKeyword"
                placeholder="搜索知识库内容..."
                @keyup.enter="handleSearch"
                style="font-size: 15px; padding: 12px;"
                :disabled="searchLoading"
              >
              <button class="ui blue button" @click="handleSearch" :disabled="searchLoading" style="font-size: 15px; padding: 0 25px;">
                <i class="search icon"></i>搜索
              </button>
            </div>
          </div>

          <div v-if="searchLoading" class="m-padded-tb-big" style="text-align: center;">
            <div class="ui active centered inline loader"></div>
            <p style="margin-top: 10px; color: #999;">正在搜索知识库...</p>
          </div>

          <div v-else-if="searchResults.length > 0" class="search-results m-padded-tb-small">
            <div class="ui info message" style="border-radius: 8px;">
              <i class="info circle icon"></i>
              找到 {{ searchResults.length }} 条相关知识
            </div>
            <div class="ui divided items">
              <div v-for="(doc, index) in searchResults" :key="index" class="item search-result-item">
                <div class="content">
                  <div class="header">
                    <i class="file alternate outline icon"></i>
                    {{ doc.title || '知识文档 #' + (index + 1) }}
                  </div>
                  <div class="meta">
                    <span v-if="doc.fileType" class="ui mini label">
                      {{ doc.fileType }}
                    </span>
                  </div>
                  <div class="description">
                    <p>{{ doc.content || '暂无预览内容' }}</p>
                  </div>
                  <div class="extra">
                    <span v-if="doc.createTime" class="m-datetime">
                      <i class="clock outline icon"></i>{{ doc.createTime }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="searchSearched" class="m-padded-tb-big" style="text-align: center;">
            <div class="ui icon header" style="color: #999;">
              <i class="search icon"></i>
              <div class="content">未找到相关知识</div>
              <div class="sub header">试试换个关键词搜索</div>
            </div>
          </div>

          <div v-else class="m-padded-tb-big" style="text-align: center;">
            <div class="ui icon header" style="color: #bbb;">
              <i class="book icon"></i>
              <div class="content">知识库搜索</div>
              <div class="sub header">输入关键词搜索知识库中的相关内容</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatMessages from '@/components/ai/ChatMessages'
import ChatInput from '@/components/ai/ChatInput'
import { chat, chatStream, createSession, getSessionHistory, searchKnowledge } from '@/api/ai'

const SESSION_STORAGE_KEY = 'ai_session_id'
const USE_STREAM = true

export default {
  name: 'KnowledgePage',
  components: { ChatMessages, ChatInput },
  data() {
    return {
      activeTab: 'chat',
      messages: [],
      chatLoading: false,
      sessionId: '',
      searchKeyword: '',
      searchResults: [],
      searchLoading: false,
      searchSearched: false
    }
  },
  created() {
    this.initSession()
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab
    },
    initSession() {
      const savedSessionId = localStorage.getItem(SESSION_STORAGE_KEY)
      if (savedSessionId) {
        this.sessionId = savedSessionId
        this.loadSessionHistory(savedSessionId)
      }
    },
    loadSessionHistory(sessionId) {
      this.chatLoading = true
      getSessionHistory(sessionId).then(res => {
        if (res.code === 200 && res.data) {
          this.messages = res.data.map(msg => ({
            role: msg.role,
            content: msg.content,
            time: this.$options.filters.dateFormat(msg.createTime, 'YYYY-MM-DD HH:mm')
          }))
        }
      }).catch(() => {
        this.sessionId = ''
        localStorage.removeItem(SESSION_STORAGE_KEY)
      }).finally(() => {
        this.chatLoading = false
      })
    },
    handleSend(question) {
      this.messages.push({
        role: 'user',
        content: question,
        time: this.$options.filters.dateFormat(new Date(), 'YYYY-MM-DD HH:mm')
      })

      this.chatLoading = true

      if (USE_STREAM) {
        const assistantMessage = {
          role: 'assistant',
          content: '',
          time: this.$options.filters.dateFormat(new Date(), 'YYYY-MM-DD HH:mm')
        }
        this.messages.push(assistantMessage)

        chatStream(
          question,
          this.sessionId,
          (content) => {
            assistantMessage.content += content
            this.$forceUpdate()
          },
          (response) => {
            if (response && response.sessionId) {
              this.sessionId = response.sessionId
              localStorage.setItem(SESSION_STORAGE_KEY, response.sessionId)
            }
            this.chatLoading = false
          },
          (error) => {
            assistantMessage.content = '网络异常，请稍后重试。'
            this.chatLoading = false
          }
        )
      } else {
        chat(question, this.sessionId).then(res => {
          if (res.code === 200 && res.data) {
            if (res.data.sessionId) {
              this.sessionId = res.data.sessionId
              localStorage.setItem(SESSION_STORAGE_KEY, res.data.sessionId)
            }
            this.messages.push({
              role: 'assistant',
              content: res.data.answer || res.data.content || '抱歉，我暂时无法回答这个问题。',
              time: this.$options.filters.dateFormat(new Date(), 'YYYY-MM-DD HH:mm')
            })
          } else {
            this.messages.push({
              role: 'assistant',
              content: res.message || '抱歉，回答生成失败，请稍后重试。',
              time: this.$options.filters.dateFormat(new Date(), 'YYYY-MM-DD HH:mm')
            })
          }
        }).catch(() => {
          this.messages.push({
            role: 'assistant',
            content: '网络异常，请稍后重试。',
            time: this.$options.filters.dateFormat(new Date(), 'YYYY-MM-DD HH:mm')
          })
        }).finally(() => {
          this.chatLoading = false
        })
      }
    },
    handleNewSession() {
      this.chatLoading = true
      createSession().then(res => {
        if (res.code === 200 && res.data) {
          this.sessionId = res.data
          localStorage.setItem(SESSION_STORAGE_KEY, res.data)
          this.messages = []
          this.$message.success('已创建新对话')
        } else {
          this.$message.error(res.message || '创建会话失败')
        }
      }).catch(() => {
        this.sessionId = ''
        this.messages = []
        localStorage.removeItem(SESSION_STORAGE_KEY)
        this.$message.error('创建会话失败，已清空当前对话')
      }).finally(() => {
        this.chatLoading = false
      })
    },
    handleClearMessages() {
      if (this.sessionId) {
        this.messages = []
        this.sessionId = ''
        localStorage.removeItem(SESSION_STORAGE_KEY)
      } else {
        this.messages = []
      }
      this.$message.success('已清空对话')
    },
    handleSearch() {
      const keyword = this.searchKeyword.trim()
      if (!keyword) {
        this.$message.warning('请输入搜索关键词')
        return
      }

      this.searchLoading = true
      this.searchSearched = false
      this.searchResults = []

      searchKnowledge(keyword, 10).then(res => {
        if (res.code === 200) {
          this.searchResults = res.data || []
        } else {
          this.searchResults = []
          this.$message.error(res.message || '搜索失败')
        }
      }).catch(() => {
        this.searchResults = []
        this.$message.error('搜索失败，请稍后重试')
      }).finally(() => {
        this.searchLoading = false
        this.searchSearched = true
      })
    }
  }
}
</script>

<style scoped>
.knowledge-page {
  min-height: 80vh;
  padding-top: 10px;
  background-color: #f5f5f5;
  padding-bottom: 40px;
}

.page-header {
  text-align: center;
  padding: 30px 0;
}

.header-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  padding: 20px 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
}

.header-icon {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon .book.icon {
  font-size: 28px;
  color: white;
  margin: 0;
}

.header-content {
  text-align: left;
}

.header-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: white;
  letter-spacing: 1px;
}

.header-subtitle {
  margin: 6px 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 300;
}

.ui.top.attached.tabular.menu {
  background: white;
  border-bottom: 2px solid #48dbfb;
}

.ui.top.attached.tabular.menu .item {
  font-size: 15px;
  padding: 14px 28px;
  color: #666;
  border: none;
  transition: all 0.3s ease;
}

.ui.top.attached.tabular.menu .item.active {
  color: #48dbfb;
  font-weight: 500;
  border-bottom: 2px solid #48dbfb;
}

.ui.bottom.attached.segment {
  background: white;
  padding: 0;
  border: 1px solid #e8e8e8;
  border-top: none;
}

.chat-panel {
  display: flex;
  flex-direction: column;
  height: 560px;
}

.chat-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.chat-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 8px 40px 12px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.search-panel {
  padding: 20px;
  min-height: 560px;
}

.search-box {
  max-width: 700px;
  margin: 0 auto 20px;
}

.search-result-item {
  padding: 16px 0 !important;
  transition: background 0.2s ease;
  border-radius: 6px;
}

.search-result-item:hover {
  background: #f8f9fa;
}

.search-result-item .header {
  font-size: 16px;
  color: #333;
  margin-bottom: 6px;
}

.search-result-item .header i {
  color: #48dbfb;
}

.search-result-item .meta {
  margin-bottom: 8px;
}

.search-result-item .meta .label {
  margin-right: 6px;
  background: #f0f0f0;
  color: #666;
}

.search-result-item .description {
  color: #555;
  line-height: 1.6;
}

.search-result-item .description p {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.search-result-item .extra {
  margin-top: 8px;
  font-size: 13px;
}

@media (max-width: 768px) {
  .knowledge-page {
    padding-top: 60px;
  }

  .chat-panel {
    height: 480px;
  }

  .search-panel {
    padding: 15px;
    min-height: 480px;
  }

  .ui.top.attached.tabular.menu .item {
    padding: 12px 18px;
    font-size: 14px;
  }
}
</style>
